import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild,
	ViewContainerRef, ElementRef, ChangeDetectorRef, HostBinding } from '@angular/core';
import { AngularFirestore } from 'angularfire2/firestore';
import { FormBuilder, FormGroup, FormControl, Validators, AbstractControl, NgForm } from '@angular/forms';
import { AlertService, AuthenticationService, LocalDataService, PaymentsService, UtilsService, CustomModalService } from '../_services/index';
import { SuiModalService } from 'ng2-semantic-ui';
import { NgxSpinnerService } from 'ngx-spinner';
import { fadeInAnimation } from '../_animations/index';
import { map, take, debounceTime } from 'rxjs/operators';
import { GooglePlaceDirective } from 'ngx-google-places-autocomplete';
import { Address } from 'ngx-google-places-autocomplete/objects/address';
import { Subscription } from 'rxjs/Subscription';
// Modals
// import { ModalDialogService, SimpleModalComponent } from 'ngx-modal-dialog';
import { ModalComponent } from '../modal/modal.component';
import { StripeService, Elements, Element as StripeElement, ElementsOptions } from 'ngx-stripe';

const cardStyle = {
	base: {
		fontFamily: "Lato, 'Helvetica Neue', Arial, Helvetica, sans-serif",
	  	fontSmoothing: 'antialiased',
	  	fontWeight: '400',
		fontSize: '13px',
	  	'::placeholder': {
        	color: '#c6c6c6'
      	}
	}
};

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	animations: [fadeInAnimation]
})

export class LoginComponent implements OnInit, OnDestroy {
	@HostBinding('@fadeInAnimation') fadeInAnimation = true;
	@HostBinding('style.display') display = 'block';

	@ViewChild('placesRef') placesRef: GooglePlaceDirective;

	// Stripe 
	elements: Elements;
	card: StripeElement; 
	cardError = false;
	cardErrorMessage: string;
	elementsOptions: ElementsOptions = {
		locale: 'en'
	};

	models: any = {};
	cardValid = false;
	showAlert = false;
	billingAddressRequired = false;
	
	activeField: string;
	currentModal;

	primaryPhoneError = false;
	resetPassword = false;
	loginActive = true;
	loginSubmitClicked = false;
	registerSubmitClicked = false;

	loginForm: FormGroup;
	registerForm: FormGroup;
	billingForm: FormGroup;

	states: Array<{ abbr: string; name: string }>;
	cardTypes: Array<{ name: string; iconClass: string }>;
	phoneTypes: Array<any>;
	months: Array<{ val: number; name: string }>;
	inputMasks: any = {};
	placesAutoCompleteOptions: any = {};
	showPassword = false;
	showConfirmPassword = false;

	// Subscriptions
	paramSubscription: Subscription;
	loginFormSubscription: Subscription;
	registerFormSubscription: Subscription;

	constructor(
		public auth: AuthenticationService,
		private cd: ChangeDetectorRef,
		private alertService: AlertService,
		private localData: LocalDataService,
		private payments: PaymentsService,
		// private modalDialogService: ModalDialogService,
		private modal: CustomModalService,
		private viewContainer: ViewContainerRef,
		private stripeService: StripeService,
		private fb: FormBuilder,
		private spinner: NgxSpinnerService,
		private utils: UtilsService,
		private afs: AngularFirestore
	) {}

	ngOnInit() {
		this.utils.showSpinner();
		this.auth.logout(); // reset login status whenever page is initialized

		this.loginForm = this.fb.group({
			email: ['', [Validators.required, Validators.email]],
			password: ['', Validators.required]
		});

		// , CustomValidator.emailAddress(this.afs) Custom Validator to check firebase for matching email address
		this.registerForm = this.fb.group({
			firstName: ['', Validators.required],
			lastName: ['', Validators.required],
			email: ['', [Validators.required, Validators.email]],
			primaryPhone: ['', Validators.required],
			primaryPhoneExt: [''],
			primaryPhoneType: ['', Validators.required],
			secondaryPhone: [''],
			secondaryPhoneExt: [''],
			secondaryPhoneType: [''],
			passwordGroup: this.fb.group({
				password: ['', Validators.required],
				confirmPassword: ['', Validators.required]
			}, { validator: this.utils.passwordMatcher }),
			addressGroup: this.fb.group({
				streetOne: ['', Validators.required],
				streetTwo: [''],
				city: ['', Validators.required],
				state: ['', Validators.required],
				zip: ['', Validators.required]
			}, {}),
			billingForm: this.fb.group({
				cardName: [''],
				billingAddressSame: ['yes'],
				addressGroup: this.fb.group({
					streetOne: ['', this.billingAddressRequired ? Validators.required : ''], // Make these required if card info is filled out
					streetTwo: [''],
					city: ['', this.billingAddressRequired ? Validators.required : ''],
					state: ['', this.billingAddressRequired ? Validators.required : ''],
					zip: ['', this.billingAddressRequired ? Validators.required : '']
				}, {})
			}),
			optOutCheck: [false],
			ageCheck: [false, Validators.requiredTrue]
		});

		this.stripeService.elements(this.elementsOptions).subscribe(elements => {
			this.elements = elements;
			// Only mount the element the first time
			if (!this.card) {
				this.card = this.elements.create('card', {
					style: cardStyle
				});
				this.card.mount('#card-info');
				this.card.on('change', (event) => {
					if (event.error) {
						this.cardError = true;
						this.cardErrorMessage = event.error.message;
						this.cardValid = false;
					} else {
						this.cardError = false;
						this.cardErrorMessage = null;
						if (event.complete) {
							if (this.registerForm.get('billingForm.cardName').value.length) {
								this.cardValid = true;
								this.billingAddressRequired = true;
							}
						} else {
							this.billingAddressRequired = false;
							this.cardValid = false;
						}
					}
				});
			}
		});

		// If there is an alert regarding either form (at the top of screen), it will go away when the user starts to type
		this.loginFormSubscription = this.loginForm.valueChanges.subscribe(data => {
			this.alertService.clear();
		});

		this.registerFormSubscription = this.registerForm.valueChanges.subscribe(data => {
			this.alertService.clear();
		});

		this.states = this.localData.getStates();
		this.cardTypes = this.localData.getCardTypes();
		this.phoneTypes = this.localData.getPhoneTypes();
		this.months = this.localData.getMonths();
		this.inputMasks = this.localData.getInputMasks();
		
		this.utils.hideSpinner(1000);
	}

	ngOnDestroy() {
		this.loginFormSubscription.unsubscribe();
		this.registerFormSubscription.unsubscribe();
	}
	
	submitForm(): void {
		if (this.loginActive) {
			this.login();
		} else {
			this.register();
		}
	}

	register(): void {
		// this.continueRegistration();
		const name = this.registerForm.get('billingForm.cardName').value;
		let billingAddress, additionalCardData;
		if (this.registerForm.get('billingForm.billingAddressSame').value === 'yes') {		
			billingAddress = this.registerForm.get('addressGroup').value;
		} else {
			billingAddress = this.registerForm.get('billingForm.addressGroup').value;
		}
		additionalCardData = this.formatDataForStripe(name, billingAddress);
		console.log('Additional card data: ', additionalCardData);
		
		this.utils.showSpinner();
		if (name) {
			this.stripeService
			// .createToken(this.card, { name })
			.createToken(this.card, additionalCardData) // pass in billing address here....
				.subscribe(result => {
					console.log('Token result: ', result);
					
					// if (result.token) {
					// 	this.cardError = false;
					// 	this.cardErrorMessage = '';
					// 	this.continueRegistration(result.token);
					// } else if (result.error) {
					// 	this.spinner.hide();
					// 	this.cardError = true;
					// 	this.cardErrorMessage = result.error.message;
					// 	console.log('Error: ', result.error.message);
					// }
				});
		} else {
			this.continueRegistration();
		}
	}

	formatDataForStripe(name, address) {
		return{
			name: name,
			address_line1: address.streetOne || null,
			address_line2: address.streetTwo || '',
			address_city: address.city || null,
			address_state: address.state || null,
			address_zip: address.zip || null,
			address_country: 'US',
		};
	}

	// continueRegistration(tokenInfo?) {
	continueRegistration() {
		if (this.registerForm.valid) {
			const rForm = this.registerForm;
			this.auth.emailSignUp( // This registers the user and adds them to the authentication table in fb
				rForm.get('email').value,
				rForm.get('passwordGroup.password').value,
				rForm.get('firstName').value + ' ' + rForm.get('lastName').value
			).then(user => {
				let primaryPhoneBase =  rForm.get('primaryPhone').value.replace(/\D/g, '') || '';
				let primaryPhoneExt = rForm.get('primaryPhoneExt').value.replace(/\D/g, '') || '';
				
				let secondaryPhoneBase =  rForm.get('secondaryPhone').value.replace(/\D/g, '') || '';
				let secondaryPhoneExt = rForm.get('secondaryPhoneExt').value.replace(/\D/g, '') || '';
				// this.auth.updateUserData(user); // This adds the user to the users table in fb
				let userData: any = {
					uid: user.uid,
					status: 'Active',
					firstName:  rForm.get('firstName').value,
					lastName: rForm.get('lastName').value,
					displayName: rForm.get('firstName').value + ' ' + rForm.get('lastName').value,
					email:  rForm.get('email').value,
					primaryPhone: primaryPhoneBase + (primaryPhoneExt ? ('x' + primaryPhoneExt) : ''),
					primaryPhoneType: rForm.get('primaryPhoneType').value || '',
					secondaryPhone: secondaryPhoneBase ? (secondaryPhoneBase + (secondaryPhoneExt ? ('x' + secondaryPhoneExt) : '')) : '',
					secondaryPhoneType: rForm.get('secondaryPhoneType').value || '',
					address: {
						streetOne: rForm.get('addressGroup.streetOne').value || '',
						streetTwo: rForm.get('addressGroup.streetTwo').value || '',
						city: rForm.get('addressGroup.city').value || '',
						state: rForm.get('addressGroup.state').value || '',
						zip: rForm.get('addressGroup.zip').value || ''
					},
					optOutFromCallsAndTexts: rForm.get('optOutCheck').value
				};
				// if (tokenInfo) {
				// 	userData.token = tokenInfo.id;
				// 	userData.defaultPaymentMethod = true;
				// 	let billingAddress;
				// 	if (rForm.get('billingForm.billingAddressSame').value === 'yes') {		
				// 		billingAddress = rForm.get('addressGroup').value;
				// 	} else {
				// 		billingAddress = rForm.get('billingForm.addressGroup').value;
				// 	}
				// 	userData.billingAddress = {
				// 		address_line1: billingAddress.streetOne || null,
				// 		address_line2: billingAddress.streetTwo || null,
				// 		address_city: billingAddress.city || null,
				// 		address_state: billingAddress.state || null,
				// 		address_zip: billingAddress.zip || null,
				// 		address_country: 'US',
				// 	};
				// 	this.finalizeRegistration(user, userData); // May want to move this billing address info into the create token function
				// }	else {
				// 		this.finalizeRegistration(user, userData);
				// }			
			}).catch(error => this.displayError(error.message, 'registration'));
		} else {
			this.registerSubmitClicked = true;
			this.validateAllFormFields(this.registerForm);
			this.displayError('Please address the errors in red and submit again.');
		}
	}

	finalizeRegistration(user, userData) {
		this.auth.updateUser(user, userData);
		this.utils.hideSpinner();
		this.alertService.success('You were registered successfully.');
		// this.registerForm.reset();
		this.utils.navigateTo('dashboard', {}, 2000);
	}

	populateBillingAddress(address) {
		this.registerForm.patchValue({
			billingForm: {
				addressGroup: {
					streetOne: address.streetOne || null,
					streetTwo: address.streetTwo || null,
					city: address.city || null,
					state: address.state || null,
					zip: address.zip || null
				}
			}
		});
	}

	login(): void {
		const lForm = this.loginForm;
		if (this.loginForm.valid) {
			this.utils.showSpinner();
			this.auth.emailLogin(
				lForm.get('email').value,
				lForm.get('password').value
			).then(user => {
				this.loginForm.reset();
				this.utils.navigateTo('dashboard');
			}).catch(error => 
				this.displayError(error.message, 'login')
			);
		} else {
			this.loginSubmitClicked = true;
			this.validateAllFormFields(this.loginForm);
			this.displayError('Please address the errors in red and submit again.');
		}
	}

	toggleTab(tabName: string) {
		this.alertService.clear();
		this.loginActive = tabName === 'register' ? false : true;
		tabName === 'register' ? this.loginForm.reset() : this.registerForm.reset();
		tabName === 'register' ? (this.loginSubmitClicked = false) : (this.registerSubmitClicked = false);
	}

	forgotPassword() {
		this.modal.customModal({
			viewContainer: this.viewContainer,
			childComponent: ModalComponent, 
			title: 'Forgot Password',
			size: 'tiny',
			templateData: {
				type: 'input',
				helpMessage: 'Enter your email address and a link to reset your password will be sent.',
				fields: [
					{ type: 'text', name: 'email', label: false, placeholder: 'Email Address', icon: 'user icon', class: 'full-width' }
				]
			}
		});
	}

	unmaskPhone(val) {
		if (!val) return false;
		let unmaskedPhone = val.replace(/\D+/g, '');
	  this.primaryPhoneError = (unmaskedPhone.length < 10) ? true : false;
	}

	errorLookup(key: string, field: string) {
		const errors = {
			email: 'Email address is not properly formatted',
			required: this.utils.capitalizeAndSplit(field) + ' is required'
		};
		return errors[key];
	}

	isFieldValid(field: string) {
		const form = this.loginActive ? this.loginForm : this.registerForm;
		const formField = form.get(field);
		const fieldName = field.split('.');
		if (!formField) {
			return false;
		}
		if (!formField.valid && formField.touched) {
			const errorText = [];
			for (const err in formField.errors) {
				errorText.push(
					this.errorLookup(err, fieldName[fieldName.length - 1])
				);
			}
			return errorText;
		} else {
			return false;
		}
	}

	validateAllFormFields(formGroup: FormGroup) {
		Object.keys(formGroup.controls).forEach(field => {
			const control = formGroup.get(field);
			if (control instanceof FormControl) {
				control.markAsTouched({ onlySelf: true });
			} else if (control instanceof FormGroup) {
				this.validateAllFormFields(control);
			}
		});
	}

	displayError(message: string, type?: string) {		
		this.spinner.hide();
		this.alertService.error((type ? ('There was an error during ' + type + '. ') : '') + message);
	}

	populateAddressForm(address, formName) {
		if (formName === 'registerForm') {
			this.registerForm.patchValue({
				addressGroup: {
					streetOne: address.streetOne || null,
					city: address.city || null,
					state: address.state || null,
					zip: address.zip || null
				}
			});
		} else {
			this.registerForm.patchValue({
				billingForm: {
					addressGroup: {
						streetOne: address.streetOne || null,
						city: address.city || null,
						state: address.state || null,
						zip: address.zip || null
					}
				}
			});
		}
	}

	public handleAddressChange(address: Address, formName) {
		this.utils.formatAddressComponents(address.address_components, (formCompatibleAddress) => {
			this.populateAddressForm(formCompatibleAddress, formName);
		});
	}

}

export class CustomValidator {
	static email(afs: AngularFirestore) {
		return (control: AbstractControl) => {
			const email = control.value.toLowerCase();
			return afs.collection('users', ref => ref.where('email', '==', email))
			.valueChanges().pipe( // Getting the observable, waits 500ms for user to stop typing, then takes the first result (if any)
				debounceTime(500),
				take(1),
				map(arr => arr.length ? { emailAddressAvailable: false } : null)
			);
		};
	}
}
